<template>
  <div class="tag-mgmt">
    <div class="tag-mgmt__shared-hint">{{ hint }}</div>
    <div ref="scrollEl" class="tag-mgmt__scroll">
      <div v-if="!tags.length && !adding" class="tag-mgmt__empty">No tags yet. Click "+ Add Tag" below.</div>
      <div
        v-for="(tag, index) in tags"
        :key="tag.uid"
        class="tag-mgmt__row"
        :class="{
          'tag-mgmt__row--dragging': dragIndex === index,
          'tag-mgmt__row--drop-target': dropTargetIndex === index,
        }"
        :draggable="dragArmedUid === tag.uid"
        @dragstart="onDragStart($event, index)"
        @dragover.prevent="onDragOver(index)"
        @dragleave="onDragLeave(index)"
        @drop.prevent="onDrop(index)"
        @dragend="onDragEnd"
        @mouseenter="hoverUid = tag.uid"
        @mouseleave="hoverUid = null">
        <v-icon icon="mdi-drag" size="18" class="tag-mgmt__drag-handle" @mousedown="dragArmedUid = tag.uid" />
        <v-menu :close-on-content-click="true">
          <template #activator="{ props: menuProps }">
            <button v-bind="menuProps" class="tag-mgmt__dot tag-mgmt__dot--clickable" type="button">
              <span class="tag-mgmt__dot-inner" :style="{ background: TAG_COLORS[tag.color] || TAG_COLORS.grey }" />
            </button>
          </template>
          <div class="tag-mgmt__palette">
            <button
              v-for="(hex, name) in TAG_COLORS"
              :key="name"
              type="button"
              class="tag-mgmt__palette-swatch"
              :class="{ 'tag-mgmt__palette-swatch--active': tag.color === name }"
              :style="{ background: hex }"
              @click="setColor(tag, name)" />
          </div>
        </v-menu>
        <input
          v-model="tag.label"
          class="tag-mgmt__input"
          placeholder="Tag label"
          @input="tag.dirty = true"
          @blur="autoKey(tag)" />
        <v-btn
          icon="mdi-close"
          variant="text"
          size="x-small"
          class="tag-mgmt__delete"
          :class="{ 'tag-mgmt__delete--visible': hoverUid === tag.uid }"
          @click="removeTag(tag)" />
      </div>

      <!-- Inline add row -->
      <div v-if="adding" class="tag-mgmt__row tag-mgmt__row--adding">
        <span class="tag-mgmt__drag-handle tag-mgmt__drag-handle--placeholder" />
        <span class="tag-mgmt__dot">
          <span class="tag-mgmt__dot-inner" :style="{ background: TAG_COLORS[newTag.color] || TAG_COLORS.grey }" />
        </span>
        <input
          ref="newLabelInput"
          v-model="newTag.label"
          class="tag-mgmt__input"
          placeholder="New tag label"
          @keyup.enter="confirmAdd"
          @keyup.escape="cancelAdd"
          @blur="confirmAdd" />
      </div>

      <button type="button" class="tag-mgmt__add-btn" :disabled="!loaded" @click="startAdd">+ Add Tag</button>
    </div>

    <!-- Save bar -->
    <div class="tag-mgmt__save-bar">
      <v-btn variant="text" size="small" :disabled="!hasChanges" @click="discardChanges">Discard changes</v-btn>
      <v-btn v-if="isCustomOrder" variant="text" size="small" @click="sortAlphabetically">Sort A to Z</v-btn>
      <transition name="tag-mgmt-fade">
        <span v-if="error" class="tag-mgmt__msg tag-mgmt__msg--error">{{ error }}</span>
        <span v-else-if="successMsg" class="tag-mgmt__msg tag-mgmt__msg--success">{{ successMsg }}</span>
      </transition>
      <v-spacer />
      <v-btn variant="flat" size="small" color="primary" :loading="saving" :disabled="!hasChanges" @click="save">
        Save
      </v-btn>
    </div>
  </div>
</template>

<script setup>
import { TAG_COLORS } from "@/constants/tagColors";
import { ref, computed, nextTick, onMounted, onBeforeUnmount } from "vue";
import { useRequirementsStore } from "@/stores";
import { settingsService } from "@/services/settingsService";
import { useSettingsSaveFlow } from "@/composables/useSettingsSaveFlow";
import { sortTagDefs, TAG_SORT_ALPHABETICAL, TAG_SORT_CUSTOM } from "@/utils/tags";

const SHARED_HINT = "Tags are shared across Intake and Prior Auth.";

const requirementsStore = useRequirementsStore();
const { saving, error, successMsg, run: runSave } = useSettingsSaveFlow();
const loaded = ref(false);
const adding = ref(false);
const hoverUid = ref(null);
const newLabelInput = ref(null);
const scrollEl = ref(null);
const newTag = ref({ label: "", color: "blue", key: "" });

const tags = ref([]);
const sortOrder = ref(TAG_SORT_ALPHABETICAL);
const initialSnapshot = ref("");
const dragArmedUid = ref(null);
const dragIndex = ref(null);
const dropTargetIndex = ref(null);
let nextUid = 1;

const isCustomOrder = computed(() => sortOrder.value === TAG_SORT_CUSTOM);
const hint = computed(() =>
  isCustomOrder.value
    ? `${SHARED_HINT} Custom order, drag to reorder.`
    : `${SHARED_HINT} Sorted A to Z, drag a tag to set a custom order.`
);

function snapshot() {
  const current = tags.value.map(({ key, label, color }) => ({ key, label, color }));
  return JSON.stringify({ tags: current, sortOrder: sortOrder.value });
}

const hasChanges = computed(() => loaded.value && snapshot() !== initialSnapshot.value);

function labelToKey(label) {
  return label
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_|_$/g, "");
}

function autoKey(tag) {
  if (tag.dirty && tag.label) {
    tag.key = labelToKey(tag.label);
  }
}

function setColor(tag, color) {
  tag.color = color;
  tag.dirty = true;
}

function startAdd() {
  if (adding.value) {
    nextTick(() => newLabelInput.value?.focus());
    return;
  }
  adding.value = true;
  newTag.value = { label: "", color: "blue", key: "" };
  nextTick(() => newLabelInput.value?.focus());
}

function confirmAdd() {
  if (!adding.value) return;
  const label = newTag.value.label.trim();
  if (!label) {
    adding.value = false;
    return;
  }
  const key = labelToKey(label);
  if (!key) {
    adding.value = false;
    return;
  }

  if (tags.value.some((t) => t.key === key)) {
    error.value = `Tag "${key}" already exists.`;
    adding.value = false;
    newTag.value = { label: "", color: "blue", key: "" };
    return;
  }

  const uid = nextUid++;
  const added = [...tags.value, { uid, key, label, color: newTag.value.color, dirty: true }];
  tags.value = isCustomOrder.value ? added : sortTagDefs(added);
  adding.value = false;
  newTag.value = { label: "", color: "blue", key: "" };
  error.value = "";
  const insertedAt = tags.value.findIndex((t) => t.uid === uid);
  nextTick(() => scrollEl.value?.querySelectorAll(".tag-mgmt__row")[insertedAt]?.scrollIntoView({ block: "nearest" }));
}

function cancelAdd() {
  adding.value = false;
  newTag.value = { label: "", color: "blue", key: "" };
}

function removeTag(tag) {
  tags.value = tags.value.filter((t) => t.uid !== tag.uid);
}

function onDragStart(event, index) {
  dragIndex.value = index;
  event.dataTransfer.effectAllowed = "move";
  event.dataTransfer.setData("text/plain", String(index));
}

function onDragOver(index) {
  if (dragIndex.value === null || dragIndex.value === index) return;
  dropTargetIndex.value = index;
}

function onDragLeave(index) {
  if (dropTargetIndex.value === index) dropTargetIndex.value = null;
}

function onDrop(toIndex) {
  const fromIndex = dragIndex.value;
  onDragEnd();
  if (fromIndex === null || fromIndex === toIndex) return;
  const items = [...tags.value];
  const [moved] = items.splice(fromIndex, 1);
  items.splice(toIndex, 0, moved);
  tags.value = items;
  sortOrder.value = TAG_SORT_CUSTOM;
}

function sortAlphabetically() {
  tags.value = sortTagDefs(tags.value);
  sortOrder.value = TAG_SORT_ALPHABETICAL;
}

function onDragEnd() {
  dragIndex.value = null;
  dropTargetIndex.value = null;
  disarmDrag();
}

function disarmDrag() {
  dragArmedUid.value = null;
}

onMounted(() => document.addEventListener("mouseup", disarmDrag));
onBeforeUnmount(() => document.removeEventListener("mouseup", disarmDrag));

async function save() {
  if (adding.value) {
    confirmAdd();
    await nextTick();
  }
  await runSave(
    async () => {
      const current = tags.value.map(({ key, label, color }) => ({ key, label, color }));
      const response = await settingsService.updateCustomTags(current, sortOrder.value);
      if (!Array.isArray(response?.tags) || response.tags.length !== current.length) {
        throw new Error("Some tags were rejected by the server. Please review and retry.");
      }
      await requirementsStore.refreshCurrentUserClientConfig();
      await loadTags();
    },
    { successText: "Tags saved.", errorText: "Failed to save tags." }
  );
}

async function discardChanges() {
  cancelAdd();
  error.value = "";
  await loadTags();
}

async function loadTags() {
  try {
    const data = await settingsService.getCustomTags();
    const loadedTags = data.intake.map((t) => ({ ...t, uid: nextUid++, dirty: false }));
    sortOrder.value = data.sortOrder || TAG_SORT_ALPHABETICAL;
    tags.value = isCustomOrder.value ? loadedTags : sortTagDefs(loadedTags);
    initialSnapshot.value = snapshot();
    loaded.value = true;
  } catch {
    error.value = "Failed to load tags.";
  }
}

loadTags();
</script>

<style lang="scss" scoped>
@import "@/styles/_variables.scss";

.tag-mgmt {
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-height: 0;
}

.tag-mgmt__shared-hint {
  flex-shrink: 0;
  font-family: $lh_font_family_dm_sans;
  font-size: 12px;
  color: $lh_color_blue_gray;
  padding: 0 16px 10px;
}

.tag-mgmt__scroll {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  padding: 0 4px;
}

.tag-mgmt__row {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px 12px;
  border-radius: 8px;
  transition: background-color $lh_transition_fast;

  &:hover {
    background: $lh_color_snowy_white;
  }
}

.tag-mgmt__row--adding {
  background: $lh_color_snowy_white;
}

.tag-mgmt__row--dragging {
  opacity: 0.4;
}

.tag-mgmt__row--drop-target {
  border-top: 2px solid $lh_color_blue;
}

.tag-mgmt__drag-handle {
  flex-shrink: 0;
  color: $lh_color_blue_gray;
  cursor: grab;

  &:active {
    cursor: grabbing;
  }
}

.tag-mgmt__drag-handle--placeholder {
  width: 18px;
}

.tag-mgmt__dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.tag-mgmt__dot--clickable {
  cursor: pointer;
  border: none;
  background: transparent;
  padding: 0;
}

.tag-mgmt__dot-inner {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  display: block;
}

.tag-mgmt__input {
  flex: 1;
  font-family: $lh_font_family_dm_sans;
  font-size: 14px;
  color: $lh_color_navy_dark;
  border: 1px solid transparent;
  border-radius: 6px;
  padding: 4px 8px;
  outline: none;
  background: transparent;
  transition: border-color $lh_transition_fast;

  &:focus {
    border-color: $lh_color_blue;
  }

  &:hover:not(:focus) {
    border-color: $lh_dash_border;
  }
}

.tag-mgmt__delete {
  color: $lh_color_blue_gray !important;
  opacity: 0;
  transition: opacity $lh_transition_fast;

  &--visible {
    opacity: 0.6;
  }

  &--visible:hover {
    color: $lh_color_red !important;
    opacity: 1;
  }
}

.tag-mgmt__palette {
  display: grid;
  grid-template-columns: repeat(8, 10px);
  gap: 8px;
  padding: 10px;
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.12);
}

.tag-mgmt__palette-swatch {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  border: 2px solid transparent;
  cursor: pointer;
  padding: 0;
  transition: border-color $lh_transition_fast;
}

.tag-mgmt__palette-swatch--active {
  border-color: $lh_color_navy_dark;
  box-shadow: 0 0 0 2px white, 0 0 0 4px currentColor;
}

.tag-mgmt__add-btn {
  font-family: $lh_font_family_dm_sans;
  font-size: 14px;
  color: $lh_color_blue_gray;
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 8px 12px;
  border-radius: 8px;
  transition: color $lh_transition_fast, background-color $lh_transition_fast;

  &:hover {
    color: $lh_color_blue;
    background: $lh_color_snowy_white;
  }
}

.tag-mgmt__save-bar {
  display: flex;
  align-items: center;
  padding: 12px 0;
  border-top: 1px solid $lh_dash_border;
  flex-shrink: 0;
  background: white;
}

.tag-mgmt__msg {
  font-family: $lh_font_family_dm_sans;
  font-size: 12px;
}

.tag-mgmt__msg--error {
  color: $lh_color_red;
}

.tag-mgmt__msg--success {
  color: $lh_color_green;
}

.tag-mgmt__empty {
  font-family: $lh_font_family_dm_sans;
  font-size: 14px;
  color: $lh_color_blue_gray;
  padding: 12px;
}
</style>
